import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { X, Send, KeyRound, Loader2, Copy, Check } from "lucide-react";
import { Button } from "./Primitives";

/**
 * OnboardModal — admin-initiated onboarding for startups OR investors.
 * `kind` = 'startup' | 'investor'.
 *
 * Behaviour by role:
 *  - super_admin → immediate creation, returns {user_id} plus a temp_password
 *                  (shown once) OR triggers an invitation email.
 *  - admin/manager/agent → queues a `pending_super_admin` approval request.
 *
 * onDone(result) is fired after a successful submit so the parent can refresh.
 */
export default function OnboardModal({ open, onClose, kind, actorRole, onDone }) {
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", note: "",
    delivery_method: "invite",
    // Startup hints
    startup_name: "", sector: "", funding_ask_inr: "", stage: "",
    // Investor hints
    investor_type: "", ticket_size_inr: "", sector_preferences: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {user_id, temp_password?, queued?}
  const [copied, setCopied] = useState(false);

  const isStartup = kind === "startup";
  const isSuperAdmin = actorRole === "super_admin";

  if (!open) return null;

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim() || undefined,
        note: form.note.trim() || undefined,
        delivery_method: form.delivery_method,
      };
      if (isStartup) {
        Object.assign(payload, {
          startup_name: form.startup_name.trim() || undefined,
          sector: form.sector.trim() || undefined,
          funding_ask_inr: form.funding_ask_inr ? Number(form.funding_ask_inr) : undefined,
          stage: form.stage.trim() || undefined,
        });
      } else {
        Object.assign(payload, {
          investor_type: form.investor_type.trim() || undefined,
          ticket_size_inr: form.ticket_size_inr ? Number(form.ticket_size_inr) : undefined,
          sector_preferences: form.sector_preferences
            ? form.sector_preferences.split(",").map((s) => s.trim()).filter(Boolean)
            : undefined,
        });
      }
      const path = isStartup ? "/admin/startups/onboard" : "/admin/investors/onboard";
      const r = await api.post(path, payload);
      setResult(r.data);
      if (r.data?.queued) {
        toast.success("Onboarding request queued — waiting for Super Admin approval.");
      } else if (r.data?.temp_password) {
        toast.success("Account created. Copy the temporary password now.");
      } else {
        toast.success("Account created and invitation email sent.");
      }
      if (typeof onDone === "function") onDone(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const copyPassword = () => {
    if (!result?.temp_password) return;
    navigator.clipboard.writeText(result.temp_password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const close = () => {
    setResult(null);
    setForm({
      name: "", email: "", mobile: "", note: "",
      delivery_method: "invite",
      startup_name: "", sector: "", funding_ask_inr: "", stage: "",
      investor_type: "", ticket_size_inr: "", sector_preferences: "",
    });
    onClose?.();
  };

  const title = isStartup ? "Onboard a Startup" : "Onboard an Investor";
  const inputCls = "w-full text-sm bg-white border border-navy/15 rounded px-3 py-2 text-ink focus:border-gold outline-none disabled:opacity-50";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid={`onboard-modal-${kind}`}
      onClick={close}
    >
      <div
        className="w-full max-w-xl bg-white rounded-xl shadow-2xl border border-navy/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy/10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-800">
              Admin · Manual Onboarding
            </div>
            <h2 className="font-heading text-lg text-ink mt-0.5">{title}</h2>
          </div>
          <button onClick={close} className="text-ink-muted hover:text-ink" data-testid="onboard-modal-close">
            <X size={18} />
          </button>
        </div>

        {result ? (
          <div className="px-5 py-5 space-y-4" data-testid="onboard-modal-result">
            {result.queued ? (
              <>
                <div className="text-sm text-ink">
                  Your onboarding request has been <b>queued for a Super Admin</b> to approve.
                  The founder / investor will only receive the email once the request is approved.
                </div>
                <div className="text-xs font-mono text-ink-muted bg-navy/[0.03] rounded px-3 py-2">
                  Request ID: {result.request_id}
                </div>
              </>
            ) : result.temp_password ? (
              <>
                <div className="text-sm text-ink">
                  Account created. <b>Copy this temporary password now</b> — it is shown once and never stored.
                </div>
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 rounded px-3 py-2.5">
                  <code className="flex-1 font-mono text-sm text-ink" data-testid="onboard-temp-password">
                    {result.temp_password}
                  </code>
                  <button onClick={copyPassword} className="text-amber-800 hover:text-amber-900"
                          data-testid="onboard-copy-password">
                    {copied ? <Check size={15} /> : <Copy size={15} />}
                  </button>
                </div>
                <div className="text-[11px] text-ink-muted">
                  User ID: <code>{result.user_id}</code> · Delivered: temporary password
                </div>
              </>
            ) : (
              <>
                <div className="text-sm text-ink">
                  Account created. An invitation email with a <b>72-hour password-set link</b> has been sent to <b>{result.email}</b>.
                </div>
                <div className="text-[11px] text-ink-muted">User ID: <code>{result.user_id}</code></div>
              </>
            )}
            <div className="pt-3">
              <Button onClick={close} variant="primary" data-testid="onboard-modal-done">Done</Button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-1">
                <div className="overline mb-1">Full Name *</div>
                <input value={form.name} onChange={(e) => patch("name", e.target.value)}
                       className={inputCls} data-testid="onboard-input-name" placeholder="Priya Sharma" />
              </label>
              <label className="col-span-1">
                <div className="overline mb-1">Email *</div>
                <input value={form.email} type="email"
                       onChange={(e) => patch("email", e.target.value)}
                       className={inputCls} data-testid="onboard-input-email" placeholder="priya@example.com" />
              </label>
              <label className="col-span-1">
                <div className="overline mb-1">Mobile</div>
                <input value={form.mobile} onChange={(e) => patch("mobile", e.target.value)}
                       className={inputCls} data-testid="onboard-input-mobile" placeholder="+91-9xxxxxxxxx" />
              </label>
              {isStartup ? (
                <>
                  <label className="col-span-1">
                    <div className="overline mb-1">Startup Name</div>
                    <input value={form.startup_name} onChange={(e) => patch("startup_name", e.target.value)}
                           className={inputCls} data-testid="onboard-input-startup-name" placeholder="Acme Labs" />
                  </label>
                  <label className="col-span-1">
                    <div className="overline mb-1">Sector</div>
                    <input value={form.sector} onChange={(e) => patch("sector", e.target.value)}
                           className={inputCls} data-testid="onboard-input-sector" placeholder="Fintech / SaaS / D2C" />
                  </label>
                  <label className="col-span-1">
                    <div className="overline mb-1">Stage</div>
                    <select value={form.stage} onChange={(e) => patch("stage", e.target.value)} className={inputCls} data-testid="onboard-input-stage">
                      <option value="">—</option>
                      <option>Idea</option><option>MVP</option><option>Pre-Seed</option>
                      <option>Seed</option><option>Pre-Series A</option><option>Series A</option>
                      <option>Series B+</option>
                    </select>
                  </label>
                  <label className="col-span-2">
                    <div className="overline mb-1">Funding Ask (₹)</div>
                    <input type="number" min="0" value={form.funding_ask_inr}
                           onChange={(e) => patch("funding_ask_inr", e.target.value)}
                           className={inputCls} data-testid="onboard-input-funding-ask" placeholder="50000000" />
                  </label>
                </>
              ) : (
                <>
                  <label className="col-span-1">
                    <div className="overline mb-1">Investor Type</div>
                    <select value={form.investor_type} onChange={(e) => patch("investor_type", e.target.value)} className={inputCls} data-testid="onboard-input-investor-type">
                      <option value="">—</option>
                      <option>Angel</option><option>HNI</option><option>Family Office</option>
                      <option>VC</option><option>PE</option><option>AIF</option>
                      <option>NBFC</option><option>Bank</option><option>Strategic</option>
                    </select>
                  </label>
                  <label className="col-span-1">
                    <div className="overline mb-1">Ticket Size (₹)</div>
                    <input type="number" min="0" value={form.ticket_size_inr}
                           onChange={(e) => patch("ticket_size_inr", e.target.value)}
                           className={inputCls} data-testid="onboard-input-ticket-size" placeholder="2500000" />
                  </label>
                  <label className="col-span-2">
                    <div className="overline mb-1">Sector Preferences (comma-separated)</div>
                    <input value={form.sector_preferences}
                           onChange={(e) => patch("sector_preferences", e.target.value)}
                           className={inputCls} data-testid="onboard-input-sector-prefs" placeholder="Fintech, HealthTech, D2C" />
                  </label>
                </>
              )}
            </div>

            <label className="block">
              <div className="overline mb-1">Note for audit log</div>
              <textarea value={form.note} onChange={(e) => patch("note", e.target.value)}
                        rows={2} className={inputCls}
                        data-testid="onboard-input-note"
                        placeholder="Referral source, meeting date, etc." />
            </label>

            <div className="border-t border-navy/10 pt-3">
              <div className="overline mb-2">Delivery method</div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button"
                        onClick={() => patch("delivery_method", "invite")}
                        className={`text-left border rounded-md px-3 py-2 ${form.delivery_method === "invite"
                          ? "border-emerald-600 bg-emerald-50/50" : "border-navy/15 hover:border-navy/25"}`}
                        data-testid="onboard-delivery-invite">
                  <div className="flex items-center gap-1.5 text-sm font-heading">
                    <Send size={13} /> Invitation email
                  </div>
                  <div className="text-[11px] text-ink-muted mt-0.5">
                    72-hour password-set link. Recommended.
                  </div>
                </button>
                <button type="button"
                        onClick={() => patch("delivery_method", "temp_password")}
                        className={`text-left border rounded-md px-3 py-2 ${form.delivery_method === "temp_password"
                          ? "border-emerald-600 bg-emerald-50/50" : "border-navy/15 hover:border-navy/25"}`}
                        data-testid="onboard-delivery-temp">
                  <div className="flex items-center gap-1.5 text-sm font-heading">
                    <KeyRound size={13} /> Temporary password
                  </div>
                  <div className="text-[11px] text-ink-muted mt-0.5">
                    Shown once in this modal. Share manually.
                  </div>
                </button>
              </div>
            </div>

            {!isSuperAdmin && (
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-2">
                Non-super-admins can only <b>request</b> onboarding — a Super Admin must approve before the user is created.
              </div>
            )}

            <div className="pt-3 flex gap-2 justify-end">
              <Button variant="ghost" onClick={close} data-testid="onboard-cancel">Cancel</Button>
              <Button variant="primary" onClick={submit} disabled={busy} data-testid="onboard-submit">
                {busy ? (<><Loader2 size={13} className="inline animate-spin mr-1" /> Working…</>)
                      : (isSuperAdmin ? "Create account" : "Request onboarding")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
